import { useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Text } from '~/eds/src/components/Text/Text';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { useGetModelsSummary } from './billing/hooks';

export const ModelStatusSummary = () => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const {
    isLoading,
    summary: { published, suspended, deletedPublishedModels },
  } = useGetModelsSummary();

  const statusLabels = ['published', 'suspended', 'deleted'];

  const getModelProps = (status: string) => {
    switch (status) {
      case 'published':
        return {
          label: intl.formatMessage({
            id: 'ModelStatusSummary.totalPublished',
            defaultMessage: 'Total Published',
            description: 'Label for total published models count',
          }),
          count: published,
        };
      case 'suspended':
        return {
          label: intl.formatMessage({
            id: 'ModelStatusSummary.totalSuspended',
            defaultMessage: 'Total Suspended',
            description: 'Label for total suspended models count',
          }),
          count: suspended,
        };
      case 'deleted':
        return {
          label: intl.formatMessage({
            id: 'ModelStatusSummary.totalDeleted',
            defaultMessage: 'Total Deleted',
            description: 'Label for total deleted models count',
          }),
          count: deletedPublishedModels,
        };
      default:
        return {
          label: '',
          count: 0,
        };
    }
  };

  const statusItems = statusLabels.map((status, index) => {
    const { label, count } = getModelProps(status);

    return (
      <Layout key={index} direction="column" align="flex-start">
        <Text variant="tiny" color="text.secondary">
          {label}
        </Text>
        {isLoading ? (
          <LoadingShimmer width="50" height="filter.height" />
        ) : (
          <Text>{formatNumber(count)}</Text>
        )}
      </Layout>
    );
  });

  return (
    <Layout direction="column" spacing={2}>
      <Text variant="body-medium">
        {intl.formatMessage({
          id: 'ModelStatusSummary.modelSummary',
          defaultMessage: 'Model Summary:',
          description: 'Header text for model status summary section',
        })}
      </Text>
      <Layout spacing={4}>{statusItems}</Layout>
    </Layout>
  );
};
